import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Card, EmptyState, Input } from '@/components/ui';
import { useT } from '@/i18n';
import { formatDateTime } from '@/lib/format';
import { AdminSkeleton, useAdminData } from './AdminLayout';

export function AdminAuditPage() {
  const t = useT();
  const { audit } = useAdminData();
  const [search, setSearch] = useState('');

  const entries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return audit;
    return audit.filter((entry) =>
      `${entry.action} ${entry.email ?? ''} ${entry.target ?? ''} ${entry.ip ?? ''}`
        .toLowerCase()
        .includes(query),
    );
  }, [audit, search]);

  return (
    <AdminSkeleton>
      <Card padding="none">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <p className="text-[15px] font-bold text-t0">{t('admin.auditTitle')}</p>
            <p className="mt-0.5 text-[11.5px] text-t2">{t('admin.auditSubtitle')}</p>
          </div>
          <div className="relative w-full max-w-xs">
            <Search size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-t2" />
            <Input
              className="pl-9"
              placeholder={t('admin.auditFilter')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t('admin.auditFilter')}
            />
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon="📋"
              title={audit.length === 0 ? t('admin.auditEmpty') : t('admin.auditNoMatch')}
              description={
                audit.length === 0 ? t('admin.auditEmptyText') : t('admin.auditNoMatchText')
              }
            />
          </div>
        ) : (
          <div className="divide-y divide-line">
            {entries.map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 px-5 py-3">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: entry.success ? 'var(--ok)' : 'var(--bad)' }}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[13px] text-t0">{entry.action}</p>
                  <p className="mt-0.5 truncate text-[11.5px] text-t2">
                    {entry.email ?? t('admin.anonymous')}
                    {entry.ip ? ` · ${entry.ip}` : ''}
                    {entry.target ? ` · ${entry.target}` : ''}
                  </p>
                </div>
                <time className="shrink-0 text-[11px] text-t2">{formatDateTime(entry.createdAt)}</time>
              </div>
            ))}
          </div>
        )}
      </Card>
    </AdminSkeleton>
  );
}
